import * as PIXI from "pixi.js";
import {Container} from "../Core/Container";
import {AbstractGameState} from "./AbstractGameState";
import {ButtonMute} from "../GameObjects/ButtonMute";
import {main} from "../Layout/KitchenScreenLayout";
import {hud} from "../Layout/HudLayout";
import {CheckmarkButton, HomeButton} from "../GameObjects/Button";
import {Timer} from "../GameObjects/Timer";
import {StateName} from "../Data/StateName";
import {Freezer, Oven} from "../GameObjects/Door";
import {CookingModel} from "../GameObjects/Cooking/CookingModel";
import {Ease} from "../Core/Animatable";
import {CookingActivity} from "../GameObjects/Cooking/CookingActivity";
import {Callback} from "../Core/Reflection";
import {ItemType} from "../Data/ItemModel";
import {CookingActivities} from "../GameObjects/Cooking/CookingActivities";
import {LunaPrompter} from "../GameObjects/LunaPrompter";
import {Keyboard} from "../GameObjects/Keyboard";
import {Sprite} from "../Core/Sprite";
import {playVO, voOptionsFacts} from "../Data/VoiceOver";
import {GameManager} from "../Managers/GameManager";

export class KitchenScreen extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new Sprite());
    public readonly activityContainer = this.main.addChild(new Container());
    public readonly freezer = this.activityContainer.addChild(new Freezer());
    public readonly oven = this.activityContainer.addChild(new Oven());
    public readonly timer = this.activityContainer.addChild(new Timer(0xffec4c, 0xcfff4c));
    public readonly freezerTimerPosition = {x: 0, y: 0};
    public readonly ovenTimerPosition = {x: 0, y: 0};
    public readonly potTimerPosition = {x: 0, y: 0};
    public cookingActivity!: CookingActivity;
    public readonly luna = this.main.addChild(new LunaPrompter());
    public readonly buttonHome = this.main.addChild(new HomeButton());
    public readonly buttonOk = this.main.addChild(new CheckmarkButton());
    public readonly buttonMute = this.addChild(new ButtonMute());

    private _model!: CookingModel;

    public init(pSize: PIXI.Point, model: CookingModel) {
        super.init(pSize, model);

        this._model = model;

        this.main.scale.set(0.5);
        main.call(this);
        hud.call(this);

        this.freezer.enabled = false;
        this.oven.enabled = false;
        this.timer.visible = false;
        this.buttonOk.visible = false;

        this.buttonHome.onClick.add(() => this.gotoHomeScreen(false), this);

        const cookingActivity = CookingActivities[model.cookingType];
        this.cookingActivity = this.activityContainer.addChild(new cookingActivity(this));
        this.playActivity();

        Keyboard.add(
            this.buttonHome,
            this.buttonOk,
            this.buttonMute
        )
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    public get top() {
        return -this._size.y;
    }

    public get bottom() {
        return this._size.y;
    }

    public get left() {
        return -this._size.x;
    }

    public get right() {
        return this._size.x;
    }

    get freezerTimer() {
        this.timer.x = this.freezerTimerPosition.x;
        this.timer.y = this.freezerTimerPosition.y;
        return this.timer;
    }

    get ovenTimer() {
        this.timer.x = this.ovenTimerPosition.x;
        this.timer.y = this.ovenTimerPosition.y;
        return this.timer;
    }

    get potTimer() {
        this.timer.x = this.potTimerPosition.x;
        this.timer.y = this.potTimerPosition.y;
        return this.timer;
    }

    confirmCookingStep(cb: Callback) {
        this.buttonOk.onClick.remove();
        this.buttonOk.onClick.add(() => {
            this.hideOkButton();
            cb();
        });
        if (!this.buttonOk.visible) {
            this.showOkButton();
        }
    }

    playFactVO(facts: string[], delay = 3, cb?: Callback) {
        this.cookingActivity.delayedCall(delay, () => {
            playVO(GameManager.instance.nextFactVO(this._model.cookingType as any, facts), {...voOptionsFacts, callback: cb});
        });
    }

    private gotoHomeScreen(submitted: boolean) {
        const itemType = this._model.cookingType as any as ItemType;
        this.gameManager.addItem({
            itemType: itemType,
            itemData: {
                cookingType: this._model.cookingType
            }
        });
        this.gotoState(StateName.Home, {activityComplete: submitted});
    }

    private playActivity() {
        this.cookingActivity.play(() => {
            this.buttonOk.onClick.remove();
            this.buttonOk.onClick.add(() => this.gotoHomeScreen(true), this);
            this.showOkButton();
            this.startActivityCompleteIdle();
        });
    }

    private showOkButton(cb?: Callback) {
        this.buttonOk.visible = true;
        this.buttonOk.moveYFrom(this._size.y + this.buttonOk.height * 0.5, 0.4, Ease.backOut, cb);
        this.buttonOk.pulse();
        Keyboard.focus(this.buttonOk);
    }

    private hideOkButton(cb?: Callback) {
        const b = this.buttonOk;
        const y = b.y;
        b.enabled = false;
        b.stopPulse();
        b.moveYTo(this._size.y + b.height * 0.5, 0.2, Ease.sineIn, () => {
            b.y = y;
            b.visible = false;
            b.enabled = true;
            if (cb) cb();
        });
        Keyboard.focus(this.buttonHome);
    }
}
